// @flow

import _ from 'lodash';
import Home from 'models/Home';
import HomeApplicationIncome from 'models/HomeApplicationIncome';
import HomeApplicationIncomeHomie from 'models/HomeApplicationIncomeHomie';
import References from 'models/References';

const formatMsiMonths = monthsHash =>
  Object.keys(monthsHash)
    .map(month => monthsHash[month] && month)
    .filter(month => !!month);

export default class HomeApplication {
  id: string;
  status: string;
  allow_billing: boolean;
  require_invoice: boolean;
  step: string;
  user: any;
  address: any;
  number_of_references: number;
  home: Home;
  home_application_income: HomeApplicationIncome;
  references: Array<References>;
  homies: Array<any>;
  invoice: any;
  income_homies: Array<HomeApplicationIncomeHomie>;
  has_pets: boolean;
  number_of_pets: number;
  has_rented_previously: boolean;
  rentedByUser: boolean;
  person_type: string;
  payment: any;
  steps: Array<string>;
  mati_client_id: string;
  monthsToPayDeposit: Array<number>;
  allowMsi: boolean;
  discountForTenants: Array<any>;
  discountsList: any;
  resolvedAt: any;
  pendingFirstPayment: boolean;
  reservationPrice: number;
  commissionsPaymentMethods: [];
  amount: number;
  lastRentId: string;
  applicationForm: any;
  agent: string;
  agency: any;
  createdAt: any;
  totalComments: number;
  preapproval: string;
  representative: string;
  risk_analyst: string;
  assignee: string;
  home_reserved_by_user: boolean;
  fromReManagement: boolean;
  source: string;
  isNoDeposit: boolean;
  riskName: string;

  constructor(data: any = {}) {
    this.id = data.id;
    this.status = data.status;
    this.step = data.step;
    this.rentedByUser = data.home_rented_by_user;
    this.home = data.home != null ? new Home(data.home) : data.home;
    this.home_application_income = new HomeApplicationIncome(
      data.home_application_income
    );
    this.address = data.address;
    this.number_of_references = data.number_of_references;
    this.user = data.user;
    this.invoice = data.invoice;
    this.references = data.references;
    this.income_homies = data.income_homies
      ? data.income_homies.map(homie => new HomeApplicationIncomeHomie(homie))
      : [];
    this.homies = data.homies;
    this.has_pets = data.has_pets;
    this.number_of_pets = data.number_of_pets;
    this.has_rented_previously = data.has_rented_previously;
    this.person_type = data.person_type;
    this.payment = data.payment;
    this.steps = data.home_application_steps;
    this.allow_billing = data.allow_billing;
    this.require_invoice = data.require_invoice;
    this.mati_client_id = data.mati_client_id;
    this.monthsToPayDeposit = data.allowed_partialization_months
      ? formatMsiMonths(data.allowed_partialization_months)
      : [];
    this.allowMsi = data.allow_monthly_owner_deposit_payment || false;
    this.discountForTenants =
      this.resolveDiscounts(data.discount_for_tenants, data.discounts_list) ||
      [];
    this.discountsList = data.discounts_list || null;
    this.resolvedAt = data.resolved_at || null;
    this.pendingFirstPayment = data.has_pending_first_payment;
    this.reservationPrice = data.reservation_price || 0;
    this.commissionsPaymentMethods = data.commissions_payment_methods || null;
    this.amount = data.amount || 0;
    this.lastRentId = data.last_rent_id || null;
    this.applicationForm = data.application_form || null;
    this.agent = this.getSuperHeroAssigned(data.user);
    this.agency = data.agency;
    this.createdAt = data.created_at;
    this.totalComments = data.total_comments || 0;
    this.preapproval = data.preapproval;
    this.representative = data.representative || 'S/D';
    this.risk_analyst = data.risk_analyst || 'N/A';
    this.assignee = data.assignee || undefined;
    this.home_reserved_by_user = data.home_reserved_by_user;
    this.fromReManagement = data.from_re_management;
    this.source = data.source;
    this.isNoDeposit = data.is_no_deposit;
    this.riskName = data.risk_name;
  }

  resolveDiscounts = (discount_for_tenants, discounts_list) => {
    if (
      discounts_list &&
      discounts_list !== null &&
      Object.keys(discounts_list).length > 0
    ) {
      return discount_for_tenants.map(discounts => {
        const rentDiscountTenant = discounts.rent_discount_for_tenant;
        if (
          discounts_list[rentDiscountTenant.applies_to] !== undefined &&
          discounts_list[rentDiscountTenant.applies_to]
            .expiration_in_minutes !== null
        ) {
          const tenantDiscounts = Object.assign(
            {},
            discounts.rent_discount_for_tenant,
            {
              expiration_in_minutes:
                discounts_list[rentDiscountTenant.applies_to]
                  .expiration_in_minutes
            }
          );
          return Object.assign(discounts, {
            rent_discount_for_tenant: tenantDiscounts
          });
        } else {
          return discounts;
        }
      });
    } else {
      return discount_for_tenants;
    }
  };

  get income() {
    return this.home_application_income;
  }

  get isBilled() {
    return !_.isNil(this.invoice);
  }

  get isReadyForBilling() {
    return this.require_invoice && this.payment && this.payment.confirmed;
  }

  static serializeForBackend(values: any): any {
    const compact = (object: Object) => {
      for (let key in object) {
        if (object[key] !== undefined) {
          return object;
        }
      }
      return undefined;
    };
    const user = {
      name: _.get(values, 'user.name'),
      last_name: _.get(values, 'user.last_name'),
      second_last_name: _.get(values, 'user.second_last_name'),
      phone_country_code: _.get(values, 'user.phone_country_code'),
      mobile_phone: _.get(values, 'user.mobile_phone'),
      email: _.get(values, 'user.email'),
      gender: _.get(values, 'user.gender'),
      rfc: _.get(values, 'user.rfc'),
      date_of_birth: _.get(values, 'user.date_of_birth'),
      country_of_birth: _.get(values, 'user.country_of_birth'),
      state_of_birth: _.get(values, 'user.state_of_birth'),
      marital_status: _.get(values, 'user.marital_status'),
      has_children: _.get(values, 'user.has_children'),
      number_of_kids: _.get(values, 'user.number_of_kids'),
      college: _.get(values, 'user.college'),
      degree: _.get(values, 'user.degree'),
      person_type: _.get(values, 'user.person_type')
    };
    const address = {
      street: _.get(values, 'address.street'),
      ext_number: _.get(values, 'address.ext_number'),
      int_number: _.get(values, 'address.int_number'),
      neighborhood: _.get(values, 'address.neighborhood'),
      town: _.get(values, 'address.town'),
      state: _.get(values, 'address.state'),
      country: _.get(values, 'address.country'),
      zip: _.get(values, 'address.zip'),
      is_foreigner: _.get(values, 'address.is_foreigner') || false
    };
    const principal_branch_address = {
      street: _.get(
        values,
        'home_application_income.principal_branch_address_street'
      ),
      ext_number: _.get(
        values,
        'home_application_income.principal_branch_address_ext_number'
      ),
      int_number: _.get(
        values,
        'home_application_income.principal_branch_address_int_number'
      ),
      neighborhood: _.get(
        values,
        'home_application_income.principal_branch_address_neighborhood'
      ),
      town: _.get(
        values,
        'home_application_income.principal_branch_address_town'
      ),
      zip: _.get(
        values,
        'home_application_income.principal_branch_address_zip'
      ),
      state: _.get(
        values,
        'home_application_income.principal_branch_address_state'
      )
    };
    const company = {
      name: _.get(values, 'home_application_income.company.name'),
      rfc: _.get(values, 'home_application_income.company.rfc'),
      business_name: _.get(
        values,
        'home_application_income.company.business_name'
      ),
      company_business_role: _.get(
        values,
        'home_application_income.company.company_business_role'
      ),
      phone: _.get(values, 'home_application_income.company.phone'),
      company_operation_years: _.get(
        values,
        'home_application_income.company.company_operation_years'
      ),
      company_type: _.get(
        values,
        'home_application_income.company.company_type'
      ),
      company_size: _.get(
        values,
        'home_application_income.company.company_size'
      ),
      invoiced_amount: _.get(
        values,
        'home_application_income.company.invoiced_amount'
      ),
      foundation_year: _.get(
        values,
        'home_application_income.company.foundation_year'
      ),
      number_of_branches: _.get(
        values,
        'home_application_income.company.number_of_branches'
      ),
      legal_representative: _.get(
        values,
        'home_application_income.company.legal_representative'
      )
    };
    const home_application_income = {
      current_living_situation: _.get(
        values,
        'home_application_income.current_living_situation'
      ),
      home_application_income_homies: _.get(
        values,
        'home_application_income.homies'
      ),
      number_of_cars: _.get(values, 'home_application_income.number_of_cars'),
      number_of_homies: _.get(
        values,
        'home_application_income.number_of_homies'
      ),
      supermarket_expenses: _.get(
        values,
        'home_application_income.supermarket_expenses'
      ),
      leisure_expenses: _.get(
        values,
        'home_application_income.leisure_expenses'
      ),
      life_insurance: _.get(
        values,
        'home_application_income.insurance_info.life_insurance'
      ),
      mayor_medical_expense: _.get(
        values,
        'home_application_income.insurance_info.mayor_medical_expense'
      ),
      minor_medical_expenses: _.get(
        values,
        'home_application_income.insurance_info.minor_medical_expenses'
      ),
      car_insurance: _.get(
        values,
        'home_application_income.insurance_info.car_insurance'
      ),
      monthly_savings: _.get(values, 'home_application_income.monthly_savings'),
      credit_card: _.get(values, 'home_application_income.credit_card'),
      other_credits: _.get(values, 'home_application_income.other_credits'),
      automotive_credit: _.get(
        values,
        'home_application_income.automotive_credit'
      ),
      mortgage_credit: _.get(values, 'home_application_income.mortgage_credit'),
      credit_card_last_digits: _.get(
        values,
        'home_application_income.credit_card_last_digits'
      ),
      credit_card_issued_months: _.get(
        values,
        'home_application_income.credit_card_issued_months'
      ),
      position: _.get(values, 'home_application_income.position'),
      everybodys_income: _.get(
        values,
        'home_application_income.everybodys_income'
      ),
      seniority_years: _.get(values, 'home_application_income.seniority_years'),
      income_source: _.get(values, 'home_application_income.income_source'),
      principal_branch_address: compact(principal_branch_address),
      company: compact(company)
    };
    return {
      home_application: {
        id: _.get(values, 'id'),
        person_type: _.get(values, 'person_type'),
        step: _.get(values, 'step'),
        user: compact(user),
        references: _.get(values, 'serialized_references'),
        address: compact(address),
        home_application_income: compact(home_application_income),
        has_rented_previously: _.get(values, 'has_rented_previously'),
        homies: _.get(values, 'homies'),
        has_pets: _.get(values, 'has_pets'),
        number_of_pets: _.get(values, 'number_of_pets'),
        application_form: _.get(values, 'serialized_application_form')
      }
    };
  }

  getSuperHeroAssigned = (user: any) => {
    const sh = (user?.super_heroes || []).find(
      x => x.super_hero_type === 'risk_analyst'
    );
    return sh ? sh.name : 'N/A';
  };
}
